/**
 * Frontend Observability Component - Grafana Faro Integration
 *
 * This component initializes Grafana Faro for Real User Monitoring (RUM).
 * It should be imported at the root of your application (_app.tsx).
 *
 * What it monitors:
 * - Page views and navigation
 * - JavaScript errors and exceptions
 * - Web Vitals (LCP, FID, CLS)
 * - User sessions and interactions
 * - Distributed tracing with backend API correlation
 *
 * @see https://grafana.com/docs/grafana-cloud/monitor-applications/frontend-observability/
 */

"use client";

import { useContext, useEffect, useRef } from "react";
import { ReactIntegration } from "@grafana/faro-react";
import type { Faro } from "@grafana/faro-web-sdk";
import { getWebInstrumentations, initializeFaro } from "@grafana/faro-web-sdk";
import { TracingInstrumentation } from "@grafana/faro-web-tracing";
import { useRouter } from "next/router";

import { UserContext } from "@/context/UserContext";

// Module-level singleton to prevent multiple Faro initializations
// This ensures Faro is initialized exactly once per application, even across React remounts
let faroSingleton: Faro | null = null;

export default function FrontendObservability() {
  const isInitialized = useRef(false);
  const faroInstance = useRef<Faro | null>(null);
  const router = useRouter();
  const { user } = useContext(UserContext);

  useEffect(() => {
    // Only run on client-side
    if (typeof window === "undefined") {
      return;
    }

    // Check singleton first - reuse existing instance if already initialized
    if (faroSingleton) {
      faroInstance.current = faroSingleton;
      isInitialized.current = true;
      return;
    }

    // Only initialize once
    if (isInitialized.current || faroInstance.current) {
      return;
    }

    // Check if running in E2E test mode - detect Playwright user agent
    // This prevents test sessions from being logged to Faro
    const userAgent = window.navigator.userAgent.toLowerCase();
    const isPlaywrightTest =
      userAgent.includes("playwright") || !!window.navigator.webdriver;

    if (isPlaywrightTest) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[FARO] Skipped: Playwright E2E test detected");
      }
      return;
    }

    // Check if Faro is enabled
    const isEnabled = process.env.NEXT_PUBLIC_FARO_ENABLED === "true";
    if (!isEnabled) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[FARO] Skipped: NEXT_PUBLIC_FARO_ENABLED is not true");
      }
      return;
    }

    // Check if Faro URL is configured
    const faroUrl = process.env.NEXT_PUBLIC_FARO_URL;
    if (!faroUrl) {
      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.warn(
          "[FARO] Skipped: NEXT_PUBLIC_FARO_URL is not configured. Set up a Faro app in Grafana Cloud and add the collector URL to your environment variables."
        );
      }
      return;
    }

    // Determine sampling rate based on environment
    // Production: 25% to stay within Grafana free tier (50k sessions/month)
    // Dev/Staging: 100% for full visibility during development
    const samplingRate =
      process.env.NEXT_PUBLIC_ENVIRONMENT === "production" ? 0.25 : 1.0;

    try {
      // In Faro v2, initializeFaro returns the Faro instance directly
      faroInstance.current = initializeFaro({
        url: faroUrl,
        app: {
          name: process.env.NEXT_PUBLIC_FARO_APP_NAME || "farmer-app",
          environment: process.env.NEXT_PUBLIC_ENVIRONMENT || "local",
          version:
            process.env.NEXT_PUBLIC_APP_VERSION ||
            process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA ||
            "dev",
        },
        sessionTracking: {
          enabled: true,
          samplingRate,
        },
        instrumentations: [
          // Get default web instrumentations (page load, network, etc.)
          ...getWebInstrumentations({
            captureConsole: false, // Disabled to prevent circular noise from Faro's own logs
          }),
          // Add distributed tracing support with API correlation
          new TracingInstrumentation({
            instrumentationOptions: {
              propagateTraceHeaderCorsUrls: [
                // Core backend API - proxy URL pattern for client-side API calls
                process.env.NEXT_PUBLIC_API_PROXY_URL
                  ? new RegExp(process.env.NEXT_PUBLIC_API_PROXY_URL)
                  : /http:\/\/localhost:3000\/api/,
                // Core backend API - actual backend if directly called
                process.env.NEXT_PUBLIC_API_BASE_URL
                  ? new RegExp(process.env.NEXT_PUBLIC_API_BASE_URL)
                  : /http:\/\/localhost:4000\/api/,
                // BX Orchestrator API - FMS integration service
                process.env.NEXT_PUBLIC_BX_ORCH_BASE_URL
                  ? new RegExp(process.env.NEXT_PUBLIC_BX_ORCH_BASE_URL)
                  : /https:\/\/bx-agent-orchestrator-.*\.run\.app/,
                // Flowise API - SAM chatbot service
                process.env.NEXT_PUBLIC_FLOWISE_API_HOST
                  ? new RegExp(process.env.NEXT_PUBLIC_FLOWISE_API_HOST)
                  : /https:\/\/.*flow\.bx\.tech/,
              ],
            },
          }),
          // Add React-specific instrumentation
          new ReactIntegration(),
        ],
      });

      // Store in module-level singleton to prevent re-initialization
      faroSingleton = faroInstance.current;
      isInitialized.current = true;

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[FARO] ✓ Frontend observability initialized", {
          app: process.env.NEXT_PUBLIC_FARO_APP_NAME || "farmer-app",
          environment: process.env.NEXT_PUBLIC_ENVIRONMENT || "local",
        });
      }
    } catch (error) {
      // Always log initialization errors, even in production
      // eslint-disable-next-line no-console
      console.error("[FARO] Failed to initialize:", error);
    }
  }, []);

  // Track Next.js client-side route changes
  useEffect(() => {
    // Only run on client-side and if Faro is initialized
    if (typeof window === "undefined" || !faroInstance.current) {
      return undefined;
    }

    const handleRouteChange = (url: string) => {
      // Update Faro's current view metadata
      faroInstance.current?.api.setView({ name: url });

      // Push a navigation event for SPA page changes
      // This creates a trackable event in Grafana's events/sessions view
      faroInstance.current?.api.pushEvent("page_view", {
        url,
        timestamp: Date.now().toString(),
        navigationType: "client_side",
      });

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[FARO] Page view tracked:", url);
      }
    };

    // Listen to Next.js router events
    router.events.on("routeChangeComplete", handleRouteChange);

    // Cleanup listener on unmount
    return () => {
      router.events.off("routeChangeComplete", handleRouteChange);
    };
  }, [router.events]);

  // Track user identification for debugging and filtering sessions
  useEffect(() => {
    // Only run on client-side and if Faro is initialized
    if (typeof window === "undefined" || !faroInstance.current) {
      return;
    }

    // Set user information when available
    if (user?.id) {
      faroInstance.current.api.setUser({
        id: user.id.toString(),
        username: user.email || undefined,
        attributes: {
          firstName: user.firstName || "",
          lastName: user.lastName || "",
          email: user.email || "",
        },
      });

      if (process.env.NODE_ENV === "development") {
        // eslint-disable-next-line no-console
        console.log("[FARO] User identified:", {
          id: user.id,
          email: user.email,
        });
      }
    }
  }, [user?.id, user?.email, user?.firstName, user?.lastName]);

  // This component doesn't render anything
  return null;
}
